import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';
import { Store } from '@ngrx/store';
import { ToastService } from '../../../core/services/toast.service';
import { CancelCondition } from '../../../core/models/entities/cancel-condition';
import { CancelConditionsService } from '../../../core/services/cancel-conditions.service';
import { cancelConditionsActions } from 'angular/app/core/store/cancel-conditions';
import { notIn } from '../../../core/services/validation.service';
import { takeUntil } from 'rxjs';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';

@Component({
  selector: 'app-cancel-condition-form',
  template: `
    <form>
      @if (!fullReimbursement) {
        <app-text-input
          label="Remboursement à hauteur"
          name="refund_percentage"
          [control]="form.get('refund_percentage')"
          append="%"
        ></app-text-input>
      }
    
      @if (canSelectUntilBookingStart) {
        <app-single-checkbox-input
          label="Jusqu'au jour de la réservation"
          name="until_booking_start"
          [control]="form.get('until_booking_start')"
        ></app-single-checkbox-input>
      }
    
      @if (!form.value.until_booking_start) {
        <app-text-input
          label="Ou jusqu'à"
          name="days_interval"
          [control]="form.get('days_interval')"
          append="jours avant la réservation"
        ></app-text-input>
      }
    
      <div class="my-8 flex items-center justify-end gap-x-6">
        <app-button
          data-cy="cancel-button"
          (click)="onCancel.emit()"
          variant="link"
          >Annuler
        </app-button>
        <app-button
          data-cy="save-button"
          icon="check"
          (click)="submit()"
          variant="secondary"
          [loading]="sendingData"
          >Valider
        </app-button>
      </div>
    </form>
    `,
  standalone: false,
})
export class CancelConditionsFormComponent
  extends DestroyableComponent
  implements OnInit
{
  @Input() fullReimbursement = false;
  @Input() cancelCondition: CancelCondition;
  @Input() cancelConditions: CancelCondition[];
  @Output() onCancel = new EventEmitter<void>();
  @Output() cancelConditionSaved = new EventEmitter<CancelCondition>();
  cancelConditionsFiltered: CancelCondition[];
  form: UntypedFormGroup;
  sendingData = false;

  constructor(
    private formValidationService: FormValidationService,
    private cancelConditionsService: CancelConditionsService,
    private store: Store,
    private toastService: ToastService,
  ) {
    super();
  }

  ngOnInit() {
    this.cancelConditionsFiltered = this.cancelConditions.filter(
      (cc) => !this.cancelCondition || this.cancelCondition.id !== cc.id,
    );
    this.form = new UntypedFormGroup({
      refund_percentage: new UntypedFormControl(
        this.cancelCondition?.refund_percentage ?? '',
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          notIn(
            this.cancelConditionsFiltered.map((cc) => cc.refund_percentage),
          ),
        ],
      ),
      until_booking_start: new UntypedFormControl(false),
      days_interval: new UntypedFormControl(
        this.cancelCondition?.days_interval ?? '',
        [
          Validators.required,
          Validators.min(
            this.fullReimbursement
              ? this.cancelConditionsFiltered[0]?.days_interval
                ? this.cancelConditionsFiltered[0]?.days_interval + 1
                : 0
              : 0,
          ),
          notIn(this.cancelConditionsFiltered.map((cc) => cc.days_interval)),
        ],
      ),
    });

    if (this.fullReimbursement) {
      this.form.get('refund_percentage').patchValue('100');
    }

    this.form
      .get('days_interval')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((daysInterval) => {
        if (daysInterval?.length > 0) {
          const days = parseInt(daysInterval, 10);
          this.form
            .get('refund_percentage')
            .setValidators([
              Validators.required,
              Validators.min(this.lowerLimit(days)),
              Validators.max(this.upperLimit(days)),
              notIn(
                this.cancelConditionsFiltered.map((cc) => cc.refund_percentage),
              ),
            ]);
          this.form.get('refund_percentage').updateValueAndValidity();
        }
      });

    this.form
      .get('until_booking_start')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((untilBookingStart) => {
        if (untilBookingStart) {
          this.form.get('days_interval').patchValue('0');
        }
        this.form.get('days_interval').updateValueAndValidity();
      });

    this.form
      .get('until_booking_start')
      .patchValue(
        this.cancelCondition
          ? this.cancelCondition.days_interval == 0
          : this.canSelectUntilBookingStart,
      );
  }

  get canSelectUntilBookingStart() {
    return this.fullReimbursement
      ? this.cancelConditionsFiltered.length === 0
      : this.cancelCondition?.days_interval == 0 ||
          !(this.cancelConditions ?? []).some((cc) => cc.days_interval == 0);
  }

  lowerLimit(days: number) {
    return (
      (this.cancelConditionsFiltered.find((cc) => cc.days_interval < days)
        ?.refund_percentage ?? 0) + 1
    );
  }

  upperLimit(days: number) {
    return this.fullReimbursement
      ? 100
      : ([...this.cancelConditionsFiltered]
          .reverse()
          .find((cc) => cc.days_interval > days)?.refund_percentage ?? 100) - 1;
  }

  async submit() {
    if (!this.formValidationService.validate(this.form)) return;

    try {
      this.sendingData = true;
      let cancelCondition: CancelCondition;
      const data = {
        ...this.form.value,
        days_interval: parseInt(this.form.value.days_interval, 10),
        refund_percentage: parseInt(this.form.value.refund_percentage, 10),
      };
      if (this.cancelCondition) {
        cancelCondition = await this.cancelConditionsService.update(
          this.cancelCondition.id,
          data,
        );
      } else {
        cancelCondition = await this.cancelConditionsService.create(data);
      }
      this.store.dispatch(
        cancelConditionsActions.upsertOne({ cancelCondition }),
      );
      this.cancelConditionSaved.emit(cancelCondition);
      this.toastService.success(
        'La condition d’annulation a été enregistré avec succès.',
      );
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    }
    this.sendingData = false;
  }
}
